import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { IFlexiblePlan } from '../FlexiblePlansSlice';
import { ISubscriptionPlan } from '../SubscriptionPlansSlice';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateGroup,
  adminGetGroups,
  adminUpdateGroup,
  adminVerifyGroupDelete,
  adminDeleteGroup,
} from './adminApi';

export const GROUPS_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export const GROUPS_FLEXIBLE_PLAN_CRITERIAS = {
  ALL: 'ALL',
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export const GROUPS_SUBSCRIPTION_PLAN_CRITERIAS = {
  ALL: 'ALL',
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export const DEFAULT_GROUP_NAME = 'Default Group';

export type TGroupsStatuses = typeof GROUPS_STATUSES[keyof typeof GROUPS_STATUSES];
export type TGroupsFlexiblePlanCriterias = typeof GROUPS_FLEXIBLE_PLAN_CRITERIAS[keyof typeof GROUPS_FLEXIBLE_PLAN_CRITERIAS];
export type TGroupsSubscriptionPlanCriterias = typeof GROUPS_SUBSCRIPTION_PLAN_CRITERIAS[keyof typeof GROUPS_SUBSCRIPTION_PLAN_CRITERIAS];

export interface IGroup {
  _id: string;
  name: string;
  status: TGroupsStatuses;
  reschedule: boolean;
  cancellation: boolean;
  oneTimePayment: boolean;
  flexiblePlan: boolean;
  subscriptionPlan: boolean;
  premiumAccess: boolean;
  flexiblePlanCriteria?: TGroupsFlexiblePlanCriterias;
  flexiblePlans?: string[] | IFlexiblePlan[];
  subscriptionPlanCriteria?: TGroupsSubscriptionPlanCriterias;
  subscriptionPlans?: string[] | ISubscriptionPlan[];
  premiumAccessStartDate?: string;
  premiumAccessEndDate?: string;
  appLogoPath?: string;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IGroupDeleteVerficationData {
  usersCount?: number;
}

export interface IInitialGroupsState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminGroupsListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IGroup>;
  };
  adminGroupsDeleteVerificationData?: IGroupDeleteVerficationData;
}

const initialState: IInitialGroupsState = {
  status: 'idle',
  adminGroupsListData: undefined,
  adminGroupsDeleteVerificationData: undefined,
};

export const GroupsSlice = createSlice({
  initialState,
  name: 'groups',
  reducers: {
    resetAdminGroupsListData: (state) => {
      state.adminGroupsListData = undefined;
    },
    resetAdminGroupsDeleteVerificationData: (state) => {
      state.adminGroupsDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetGroups.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminGroupsListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminGroupsListData = newAdminGroupsListData;
      })
      .addCase(adminCreateGroup.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminGroupsListData) {
          const newAdminGroupsListData = {
            ...state.adminGroupsListData,
          };

          newAdminGroupsListData.list.unshift(result);

          if (newAdminGroupsListData.totalDocs) {
            newAdminGroupsListData.totalDocs += 1;
          } else {
            newAdminGroupsListData.totalDocs = 1;
          }

          state.adminGroupsListData = newAdminGroupsListData;
        }
      })
      .addCase(adminUpdateGroup.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminGroupsListData &&
          state.adminGroupsListData.list &&
          state.adminGroupsListData.list.length
        ) {
          const newAdminGroupsListData = {
            ...state.adminGroupsListData,
          };

          const index = newAdminGroupsListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminGroupsListData.list[index] = result;
          }

          state.adminGroupsListData = newAdminGroupsListData;
        }
      })
      .addCase(adminDeleteGroup.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminGroupsListData &&
          state.adminGroupsListData.list &&
          state.adminGroupsListData.list.length
        ) {
          const newAdminGroupsListData = {
            ...state.adminGroupsListData,
          };

          newAdminGroupsListData.totalDocs = newAdminGroupsListData.totalDocs
            ? newAdminGroupsListData.totalDocs - 1
            : 0;

          newAdminGroupsListData.list = newAdminGroupsListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminGroupsListData = newAdminGroupsListData;
        }
      })
      .addCase(adminVerifyGroupDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminGroupsDeleteVerificationData = result;
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        // GROUPS
        adminCreateGroup,
        adminGetGroups,
        adminUpdateGroup,
        adminDeleteGroup,
        adminVerifyGroupDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        // GROUPS
        adminCreateGroup,
        adminGetGroups,
        adminUpdateGroup,
        adminDeleteGroup,
        adminVerifyGroupDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        // GROUPS
        adminCreateGroup,
        adminGetGroups,
        adminUpdateGroup,
        adminDeleteGroup,
        adminVerifyGroupDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getGroupsState = (state: RootState): RootState['groups'] =>
  state.groups;

export const {
  resetAdminGroupsListData,
  resetAdminGroupsDeleteVerificationData,
} = GroupsSlice.actions;

export * from './adminApi';
export default GroupsSlice.reducer;
